import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { setAccessToken } from "../api";
import { authApi } from "../services/auth";
import { usersApi } from "../services/users";
import { AuthContext } from "../context/AuthContext";
import {
  openStripeUrl,
  stripeClientFields,
  IS_NATIVE,
  getAppStripeNonce,
  stashAppStripeNonce,
  clearAppStripeNonce,
  onStripeBrowserClosed,
  wasStripeReturnJustHandled,
} from "../lib/stripeFlow";
import FinishSetupScreen from "../components/FinishSetupScreen";
import StripeWaitScreen from "../components/StripeWaitScreen";
import { track } from "../lib/analytics";
import "./StripeCallbackPage.css";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";

// Onboarding's Step6Complete stashes the talent's avatar here before the
// Stripe redirect because the in-memory File dies with the page unload. We
// finish the upload once the talent is back and authenticated.
// Mirrored in src/pages/onboarding/steps/Step6Complete.tsx — keep in sync.
//
// The slug isn't stashed: the User model's pre-validate hook re-derives slug
// from name (and adds a collision counter that's formatted differently from
// the client-side picker), so we can't trust the locally-chosen slug. The
// caller passes in the server-canonical slug from /auth/user instead.
const PENDING_AVATAR_KEY = "pendingAvatarUpload";
const PENDING_AVATAR_TTL_MS = 24 * 60 * 60 * 1000;

type StashedAvatar = {
  dataUrl?: string;
  name?: string;
  ts?: number;
  /** The email of the sign-up that left it (from its sign-in token). */
  email?: string;
};

// An email the way the server stores it (server lib/normalizeEmail.js):
// lowercase, and Gmail ignores dots in the name.
const accountEmail = (raw: string | undefined): string => {
  const email = String(raw || "").trim().toLowerCase();
  const [local, domain] = email.split("@");
  return domain === "gmail.com" || domain === "googlemail.com" ? `${local.replace(/\./g, "")}@${domain}` : email;
};

const uploadStashedAvatar = async ({ slug, email: accountOf }: { slug: string; email?: string }): Promise<void> => {
  if (!slug) return;

  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PENDING_AVATAR_KEY);
  } catch {
    return;
  }
  if (!raw) return;

  try {
    const parsed = JSON.parse(raw) as StashedAvatar;
    const { dataUrl, name, ts, email } = parsed || {};
    if (!dataUrl || !ts) return;
    if (Date.now() - ts > PENDING_AVATAR_TTL_MS) return;
    // Only onto the account whose sign-up left it: an abandoned sign-up's
    // photo landed on the next account to finish Stripe on this device. No
    // email (a stash from before this check) = can't tell = dropped
    // (2026-10-10).
    if (!email || accountEmail(email) !== accountEmail(accountOf)) return;

    const blob = await (await fetch(dataUrl)).blob();
    const formData = new FormData();
    // The server's multer config derives the saved-file extension from the
    // original filename, so preserve it from the stash to avoid extension-less
    // avatars on disk.
    formData.append("avatar", blob, name || "avatar");
    await usersApi.update(slug, formData);
  } catch {
    // Best-effort: a failed upload just means the talent re-uploads from
    // /profile later. Never block the post-Stripe login.
  } finally {
    try {
      localStorage.removeItem(PENDING_AVATAR_KEY);
    } catch {}
  }
};

// Possible screens. There is deliberately NO "error" / "something went wrong"
// state — every server outcome is either a silent action (log in & redirect to
// the app, or bounce straight back into Stripe) or one of the friendly,
// actionable screens below. See server routes/auth.js #complete-registration and
// CLAUDE.md "Error/failure screens — cover EVERY path, prefer silent recovery".
type Status =
  | "loading"
  | "redirecting"
  | "incomplete"
  | "rejected"
  | "needs_login"
  | "not_found";

// Silently retry a transient/network failure this many times before falling back
// to the friendly "log back in" screen. The post-Stripe state is reconciled
// server-side regardless (account.updated webhook + login-resume), so even a
// retry that never succeeds is not a dead-end — the talent just logs in to finish.
const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1500;

const StripeCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<Status>("loading");
  // Rejection reason from the server, shown in the contact-support copy.
  const [rejectionReason, setRejectionReason] = useState<string>("");
  // Fresh one-tap Stripe link for the "Almost there" card (Option B). When the
  // server can re-mint a link we send the talent straight back into Stripe with
  // one tap; otherwise the card falls back to the login route.
  const [incompleteUrl, setIncompleteUrl] = useState<string>("");
  const navigate = useNavigate();
  const { login, user: authedUser } = useContext(AuthContext);

  // ── Sheet-close recovery (build 41 black-screen fix) ─────────────────────
  // On native, Stripe opens in the in-app browser sheet presented OVER this
  // page — so when the sheet dismisses, THIS page is what the talent is
  // looking at. Every other place that opens Stripe (Step6Complete,
  // FinishStripePage, useCashout) already watches for that close; these two
  // call sites did not, which left a talent who finished Stripe (or tapped
  // Done) staring at a "Taking you to Stripe…" screen that could never move
  // again — the reported black screen, escapable only by restarting the app.
  // Re-running the exchange is safe: the server tolerates a spent nonce.
  const [retryTick, setRetryTick] = useState(0);
  const sheetWatchRef = useRef<null | (() => void)>(null);
  const armSheetCloseRecovery = useCallback(() => {
    sheetWatchRef.current?.();
    const stop = onStripeBrowserClosed(() => {
      stop();
      sheetWatchRef.current = null;
      // The universal link beat us to it and is already routing the return.
      if (wasStripeReturnJustHandled()) return;
      setStatus("loading");
      setRetryTick((n) => n + 1);
    });
    sheetWatchRef.current = stop;
  }, []);
  useEffect(() => () => sheetWatchRef.current?.(), []);

  // Native hand-off (hardened for the stranded-signup bug):
  // when this page renders in a BROWSER with ?app=1, the registration started
  // inside the iOS app and we're most likely inside its in-app browser sheet
  // (the universal link didn't auto-fire). STRICT rule: with app=1 this page
  // may never consume the nonce or start a browser session — its only job is
  // handing the talent back into the app. The old "Continue in the browser
  // instead" escape hatch is exactly how one signup ended up logged into
  // the web with their profile photo stranded in the app's storage — removed.
  const appHandoff = !IS_NATIVE && searchParams.get("app") === "1";
  // Stripe's refresh_url bounce (expired/re-mint link) arrives with refresh=1
  // — same hand-off, honest copy ("continue", not "all set").
  const isRefreshBounce = searchParams.get("refresh") === "1";

  // Custom-scheme URL carrying the untouched nonce/token into the app. The
  // `app` flag itself is dropped — inside the app it has no meaning.
  const handoffUrl = React.useMemo(() => {
    const p = new URLSearchParams(searchParams);
    p.delete("app");
    return `requestapp://register/stripe/callback?${p.toString()}`;
  }, [searchParams]);

  // Growth funnel: back from Stripe (any outcome) = an onboarding step.
  useEffect(() => {
    track("onboarding_step_completed", { props: { step: "stripe_returned" } });
  }, []);

  useEffect(() => {
    if (!appHandoff) return;
    // Best-effort auto-open — iOS shows its "Open in Request App?" dialog. If
    // it's silently ignored (needs a user gesture), the button is the path.
    const t = setTimeout(() => {
      window.location.href = handoffUrl;
    }, 400);
    return () => clearTimeout(t);
  }, [appHandoff, handoffUrl]);

  useEffect(() => {
    if (appHandoff) return;
    // Authenticated-flow return (Finish-Stripe / cashout-time connect)
    // delivered back into the app by the hand-off: only AuthContext's boot
    // sequence knows how to exchange ?stripeReturn — full in-place reload,
    // exactly like DeepLinkHandler's /profile?stripeReturn path.
    const stripeReturn = searchParams.get("stripeReturn");
    if (IS_NATIVE && stripeReturn) {
      window.location.href = `/profile?stripeReturn=${encodeURIComponent(stripeReturn)}`;
      return;
    }
    const token = searchParams.get("token");
    // Signed-in native user with nothing to exchange (e.g. the refresh bounce
    // of an authenticated Finish-Stripe link): home is /profile — and any
    // leftover signup stash is stale by definition.
    if (IS_NATIVE && authedUser && !searchParams.get("nonce") && !token) {
      clearAppStripeNonce();
      navigate("/profile", { replace: true });
      return;
    }
    // In-app the URL nonce can be missing (refresh-bounce hand-off, cold-start
    // resume after an app kill) — fall back to the app-held copy. The server
    // tolerates spent nonces, so a stale stash still identifies the talent.
    const nonce = searchParams.get("nonce") || (IS_NATIVE ? getAppStripeNonce() : null);
    let cancelled = false;

    // Silent success: upload the stashed avatar, log in, land on Step 6.
    const finalizeSuccess = async (accessToken: string): Promise<boolean> => {
      setAccessToken(accessToken);
      try {
        const userData = await authApi.user();
        await uploadStashedAvatar(userData);
        login(userData, accessToken);
        clearAppStripeNonce();
        track("onboarding_completed");
        // Back to /register so onboarding lands on Step 6 (AccessCode). If the
        // talent is already activated, Step 6 self-skips to /profile.
        navigate("/register", { replace: true });
        return true;
      } catch {
        return false;
      }
    };

    // Map a server outcome to UI. Returns true when handled (terminal for this
    // attempt), false when the body wasn't recognised (caller decides fallback).
    // Used for BOTH the 2xx body AND an error-response body, so a known outcome
    // delivered as a non-2xx is never misread as a transient failure.
    const routeOutcome = async (data: any): Promise<boolean> => {
      switch (data?.msg) {
        case "registration_complete":
          if (data.accessToken && (await finalizeSuccess(data.accessToken))) return true;
          if (!cancelled) setStatus("needs_login");
          return true;
        case "stripe_continue":
          // No screen — quietly send them straight into Stripe (in-app
          // browser sheet on native, full redirect on web). Fresh app copy of
          // the completion key first, so the next sheet-close self-completes.
          if (data.appNonce) stashAppStripeNonce(data.appNonce);
          if (data.stripeUrl) {
            if (!cancelled) setStatus("redirecting");
            armSheetCloseRecovery();
            openStripeUrl(data.stripeUrl);
            return true;
          }
          if (!cancelled) setStatus("needs_login");
          return true;
        case "stripe_incomplete":
          if (data.appNonce) stashAppStripeNonce(data.appNonce);
          if (!cancelled) {
            setIncompleteUrl(data.stripeUrl || "");
            setStatus("incomplete");
          }
          return true;
        case "stripe_rejected":
          clearAppStripeNonce();
          if (!cancelled) {
            setRejectionReason(data.reason || "");
            setStatus("rejected");
          }
          return true;
        case "needs_login":
          // The server could not identify anyone from this key (purged after
          // its 60-min life) — holding on to it only re-runs this dead end on
          // the next cold start. Login-resume finishes them off from /login.
          clearAppStripeNonce();
          if (!cancelled) setStatus("needs_login");
          return true;
        case "user_not_found":
          clearAppStripeNonce();
          if (!cancelled) setStatus("not_found");
          return true;
        default:
          return false;
      }
    };

    const run = async (attempt: number) => {
      try {
        try {
          await authApi.csrfToken();
        } catch {}

        const q = new URLSearchParams();
        if (nonce) q.set("nonce", nonce);
        else if (token) q.set("token", token);

        // Native sends client:"app" so any re-minted Stripe link keeps the
        // app=1 return tag (lib/stripeFlow.ts).
        const data = await authApi.completeRegistration(q.toString(), { ...stripeClientFields() });
        if (cancelled) return;
        if (await routeOutcome(data)) return;
        // Unknown 2xx body — never dead-end; the login flow self-heals.
        setStatus("needs_login");
      } catch (err: any) {
        if (cancelled) return;
        const data = err?.response?.data;
        // A known outcome can arrive as a non-2xx (rejected = 400, not_found = 404).
        if (data && (await routeOutcome(data))) return;
        // Genuinely transient (network / 5xx / unexpected) — retry a few times,
        // then fall back to the friendly login screen. Stripe state reconciles
        // server-side regardless, so this is never a dead-end.
        if (attempt < MAX_ATTEMPTS) {
          setTimeout(() => {
            if (!cancelled) run(attempt + 1);
          }, RETRY_DELAY_MS);
          return;
        }
        setStatus("needs_login");
      }
    };

    run(1);
    return () => {
      cancelled = true;
    };
    // One exchange per arrival, plus one per sheet-close recovery
    // (retryTick). login, navigate and authedUser are read at effect time;
    // re-running for any other reason would post the registration twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, appHandoff, retryTick]);

  // The hand-off card (browser + app=1): Stripe is done — send them back into
  // the app with the nonce intact. The ghost link is the safety hatch for the
  // odd case this renders somewhere the app can't answer the custom scheme.
  if (appHandoff) {
    return (
      <div className="stripe-callback-container">
        <div className="stripe-box stripe-incomplete-card">
          <h2 className="stripe-ic-title">
            {isRefreshBounce ? (
              <><T k="stripeCallbackPage.oneMoreStep" tags={{ 0: (c) => <span className="stripe-ic-emoji" role="img" aria-label={tr("stripeCallbackPage.headphones")}>{c}</span> }} /></>
            ) : (
              <><T k="stripeCallbackPage.allSet" tags={{ 0: (c) => <span className="stripe-ic-emoji" role="img" aria-label={tr("stripeCallbackPage.party")}>{c}</span> }} /></>
            )}
          </h2>
          <p className="stripe-ic-message">
            {isRefreshBounce ? tr("stripeCallbackPage.headBackToThe") : tr("stripeCallbackPage.headBackToThe2")}
          </p>
          <a className="stripe-connect-button" href={handoffUrl} style={{ textDecoration: "none" }}>
            <span className="stripe-connect-label">{tr("stripeCallbackPage.returnToRequestApp")}</span>
            <svg className="stripe-connect-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </a>
        </div>
      </div>
    );
  }

  if (status === "loading" || status === "redirecting") {
    return (
      <StripeWaitScreen
        message={status === "redirecting" ? tr("stripeCallbackPage.takingYouToStripe") : tr("stripeCallbackPage.finishingYourSetup")}
      />
    );
  }

  // Stripe genuinely still needs one more detail. One tap takes them straight
  // back into Stripe via the freshly-minted link; if we couldn't mint one, the
  // button falls back to logging in (login-resume picks it up). Renders the ONE
  // shared finish-setup screen (punch list) — same layout as
  // FinishStripePage, no bespoke "Almost there" card.
  if (status === "incomplete") {
    const onComplete = () => {
      if (incompleteUrl) {
        // Same recovery: closing the sheet re-checks where they actually got
        // to, instead of leaving this card sitting there as a dead button.
        armSheetCloseRecovery();
        openStripeUrl(incompleteUrl);
      } else {
        navigate("/login");
      }
    };
    return (
      <FinishSetupScreen
        ctaTitle={tr("stripeCallbackPage.addBankDetails")}
        ctaSub={
          <>
            <T k="stripeCallbackPage.secureFastPayoutsMin" tags={{ 0: (c) => <span className="step5-choice-sub-dot">{c}</span> }} />
          </>
        }
        onCta={onComplete}
        onBack={() => navigate("/")}
      />
    );
  }

  // We couldn't identify the talent from the callback (no/expired token, no
  // session) — friendliest forward path is to log back in, where login-resume
  // detects their Stripe state and finishes them off. Never a dead-end. Same
  // shared finish-setup screen; only the CTA differs.
  if (status === "needs_login") {
    return (
      <FinishSetupScreen
        ctaTitle={tr("stripeCallbackPage.logInToFinish")}
        ctaSub={tr("stripeCallbackPage.yourSetupPicksUp")}
        onCta={() => navigate("/login")}
        onBack={() => navigate("/")}
      />
    );
  }

  // Terminal rejection (KYC failure, sanctions, prohibited industry). Re-entering
  // Stripe would bounce to the same rejection, so the only forward path is
  // contact-support. Mirrors StripeRejectedPage (the post-login equivalent).
  if (status === "rejected") {
    return (
      <div className="stripe-callback-container">
        <div className="stripe-box">
          <h2 className="stripe-incomplete-title">{tr("stripeCallbackPage.weCouldnTVerify")}</h2>
          <p className="stripe-incomplete-message">
            {tr("stripeCallbackPage.stripeWasnTAble")}
          </p>
          <a
            className="stripe-return-button"
            href="mailto:support@requesta.app?subject=Account%20verification%20help"
            style={{ display: "inline-block", textDecoration: "none" }}
          >
            {tr("stripeCallbackPage.contactSupport")}
          </a>
          {rejectionReason && (
            <p style={{ marginTop: 16, fontSize: "0.78rem", color: "rgba(229,231,235,0.45)", fontFamily: "ui-monospace, Menlo, monospace" }}>
              {tr("stripeCallbackPage.ref", { rejectionReason })}
            </p>
          )}
        </div>
      </div>
    );
  }

  // status === "not_found": the callback resolved to a user that no longer exists
  // (very rare; the server also alerts the founder). Friendly + actionable.
  return (
    <div className="stripe-callback-container">
      <div className="stripe-box">
        <h2 className="stripe-incomplete-title">{tr("stripeCallbackPage.weCouldnTFind")}</h2>
        <p className="stripe-incomplete-message">
          {tr("stripeCallbackPage.looksLikeYourAccount")}
        </p>
        <button
          className="stripe-return-button"
          onClick={() => navigate("/register")}
          style={{ display: "inline-block" }}
        >
          {tr("stripeCallbackPage.createYourAccount")}
        </button>
        <p style={{ marginTop: 16, fontSize: "0.85rem" }}>
          <a className="stripe-link" href="mailto:support@requesta.app?subject=Help%20finishing%20signup">
            {tr("stripeCallbackPage.contactSupport")}
          </a>
        </p>
      </div>
    </div>
  );
};

export default StripeCallbackPage;
